import { pr, rcLogger, timeCall } from '@readcloud/common';
import { ErrorType, PDFData } from '@readcloud/data';
import {
  tauriFetch,
  isFileCached,
  reportNetworkError,
  getIsOnline,
  resolveTauriBookAssetUrl,
  resolveTauriBookAssetInfo,
} from '@readcloud/fetch-wrapper';
import { FetchOptions } from '@readcloud/fetch-wrapper/lib/types';
import { CustomPDFDataRangeTransport } from './CustomPDFDataRangeTransport';
import { getBookTypeFromMime, getType } from './documentUtils';

const processChunk = async ({
  data,
  counter,
  isbn,
  minor,
  longId,
  onError,
}: {
  data: ArrayBuffer;
  counter: number;
  onError: (error: ErrorType) => void;
  isbn?: string;
  minor?: number;
  longId?: string;
}): Promise<ArrayBuffer | Uint8Array> => {
  rcLogger.debug('Minor', minor);
  switch (`${minor}`) {
    case '2':
      rcLogger.debug('Beggining method = 2 RCS Process');
      if (!longId || !isbn) {
        throw new Error('Invalid instantiation parameters');
      }
      try {
        const enhancedData: ArrayBuffer | Uint8Array | { error: any } =
          await timeCall('RCS Process')(() =>
            isbn && longId && minor
              ? pr(isbn, data, longId, minor, counter)
              : Promise.reject(new Error('Invalid instantiation parameters'))
          );
        if ('error' in enhancedData) {
          throw enhancedData.error;
        }
        rcLogger.debug('RCS Process Finished');
        return enhancedData;
      } catch (e) {
        rcLogger.debug('RCS Process failed');
        onError({ error: e as Error, type: 'ProcessError' });
        return data;
      }

    default:
      return data;
  }
};

export const prepareTauriDocument = async (props: {
  fileUrl: string;
  isbn?: string;
  minor?: number;
  longId?: string;
  onBeforePrepare?: () => Promise<any>;
  onProgress?: (progress: number) => void;
  // Optional: no-op in Tauri path for now
  signal?: AbortSignal;
  onDownloadStart?: (info: {
    id: string;
    url: string;
    filename: string;
  }) => void;
}): Promise<{
  data: PDFData;
  type: string;
  error?: ErrorType;
  loading: boolean;
  progress: number;
}> => {
  // Reset state
  let error: ErrorType | undefined;
  let loading = true;
  let progress = 0;
  let type: string | undefined;
  let data: PDFData | undefined;

  try {
    // Check if we're offline and the file isn't cached locally
    // Use fetch-wrapper's online state which is more reliable than navigator.onLine in Tauri
    const isOnline = getIsOnline();
    const isCached = await isFileCached(props.fileUrl);

    rcLogger.debug('prepareTauriDocument: isOnline:', isOnline, 'isCached:', isCached);

    if (!isOnline && !isCached) {
      rcLogger.debug(
        'Offline and file not cached, returning OfflineNotCached error'
      );
      return {
        data: undefined,
        type: undefined,
        error: { type: 'OfflineNotCached' },
        loading: false,
        progress: 0,
      };
    }

    if (props.onBeforePrepare) {
      await props.onBeforePrepare();
    }

    rcLogger.debug('Beginning document processing in Tauri environment');

    // Get the file type from extension
    const bookType = getType(props.fileUrl);

    // Check if processing is required
    // Only check for minor values that are actually handled in processChunk
    const requiresProcessing = () =>
      !!props.minor && props.minor.toString() === '2';

    // Fast path for cached, non-DRM PDFs: hand PDF.js an asset:// URL so the
    // webview streams the bytes through Tauri's asset protocol (no IPC copy,
    // Range requests supported). EPUBs still go through the byte path below
    // (separate loader).
    if (isCached && bookType === 'PDF' && !requiresProcessing()) {
      const assetUrl = await resolveTauriBookAssetUrl(props.fileUrl);
      if (assetUrl) {
        rcLogger.debug(
          'prepareTauriDocument: serving cached PDF via asset URL',
          assetUrl
        );
        return {
          data: assetUrl,
          type: bookType,
          error: undefined,
          loading: false,
          progress: 0,
        };
      }
    }

    // Cached DRM PDFs: same idea, but we need a range transport so each chunk
    // can be decrypted by processChunk before PDF.js sees it. The transport
    // fetches encrypted byte ranges through the asset protocol (no IPC copy,
    // native Range support) and runs pr() per chunk. File is kept encrypted
    // at rest — the decrypt step runs on each read.
    //
    // Android gate: Tauri v2's asset protocol on Android does not emit CORS
    // headers (`Access-Control-Allow-Origin` etc.), so only the first
    // cross-origin range fetch succeeds; subsequent ones are blocked by
    // Chromium's CORS enforcement. PDF.js needs the trailing xref chunk on
    // request #2, so loading fails. Until we register a custom Rust protocol
    // with CORS headers, fall back to the byte path on Android.
    let platformName: string | undefined;
    try {
      const osMod: any = await import('@tauri-apps/plugin-os');
      const platform = osMod.platform;
      platformName =
        typeof platform === 'function'
          ? await Promise.resolve(platform())
          : undefined;
    } catch {
      // best-effort; safe default below treats unknown platform as mobile
    }
    // Exclude both mobile platforms. Android's asset protocol doesn't emit
    // CORS headers so only the first cross-origin range fetch succeeds; iOS
    // is untested. If platform lookup failed, assume mobile (safe default).
    const supportsAssetRangeTransport =
      platformName !== undefined &&
      platformName !== 'android' &&
      platformName !== 'ios';

    if (
      isCached &&
      bookType === 'PDF' &&
      requiresProcessing() &&
      supportsAssetRangeTransport
    ) {
      const info = await resolveTauriBookAssetInfo(props.fileUrl);
      if (info) {
        rcLogger.debug(
          'prepareTauriDocument: serving cached DRM PDF via range transport',
          { assetUrl: info.assetUrl, size: info.size }
        );
        const transport = new CustomPDFDataRangeTransport(
          info.assetUrl,
          info.size,
          undefined, // default headers (Range / Content-Range)
          () => {
            error = {
              type: 'DownloadError',
              error: {
                name: '',
                message: 'Failed to read cached document.',
              },
            };
          },
          () => loading,
          (chunk, counter) =>
            processChunk({
              data: chunk,
              counter,
              isbn: props.isbn,
              longId: props.longId,
              minor: props.minor,
              onError: (e) => {
                error = e;
              },
            })
        );
        return {
          data: transport,
          type: bookType,
          error: undefined,
          loading: false,
          progress: 0,
        };
      }
    }

    rcLogger.debug('Loading PDF via tauriFetch', props.fileUrl);

    const response = await tauriFetch(props.fileUrl, {
      method: 'GET',
      mode: 'cors',
      redirect: 'follow',
      headers: {
        accept: '*/*',
      },
      onProgress: props.onProgress,
      onDownloadStart: props.onDownloadStart,
    } as FetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the ArrayBuffer from the response
    const rawData = await response.arrayBuffer();

    // Process the data if needed (decryption), otherwise use the raw data
    let processedData: ArrayBuffer | Uint8Array;
    if (requiresProcessing()) {
      rcLogger.debug(
        'prepareTauriDocument: Processing required, calling processChunk'
      );
      processedData = await processChunk({
        data: rawData,
        counter: 0,
        isbn: props.isbn,
        longId: props.longId,
        minor: props.minor,
        onError: (e) => {
          error = e;
        },
      });
    } else {
      rcLogger.debug(
        'prepareTauriDocument: No processing required, using raw data'
      );
      processedData = rawData;
    }

    // Debug processed data info
    rcLogger.debug(
      'prepareTauriDocument: processedData type:',
      typeof processedData
    );
    rcLogger.debug(
      'prepareTauriDocument: processedData instanceof ArrayBuffer:',
      processedData instanceof ArrayBuffer
    );
    rcLogger.debug(
      'prepareTauriDocument: processedData instanceof Uint8Array:',
      processedData instanceof Uint8Array
    );
    rcLogger.debug(
      'prepareTauriDocument: processedData byteLength:',
      processedData instanceof ArrayBuffer ? processedData.byteLength : 'N/A'
    );
    rcLogger.debug(
      'prepareTauriDocument: processedData length (if Uint8Array):',
      processedData instanceof Uint8Array ? processedData.length : 'N/A'
    );

    // Check if processed data is valid
    if (!processedData) {
      rcLogger.error(
        'prepareTauriDocument: processedData is null or undefined'
      );
      throw new Error('Processed PDF data is null or undefined');
    }

    if (
      processedData instanceof ArrayBuffer &&
      processedData.byteLength === 0
    ) {
      rcLogger.error(
        'prepareTauriDocument: processedData ArrayBuffer is empty'
      );
      throw new Error('Processed PDF data is empty');
    }

    if (processedData instanceof Uint8Array && processedData.length === 0) {
      rcLogger.error('prepareTauriDocument: processedData Uint8Array is empty');
      throw new Error('Processed PDF data is empty');
    }

    data = processedData;
    type = bookType;
    loading = false;

    return {
      data,
      type,
      error,
      loading,
      progress,
    };
  } catch (e) {
    rcLogger.error('Error preparing document:', e);

    // Debug: Log the full error object to understand what we're receiving
    // Safely stringify error to handle circular references
    let stringified = 'N/A';
    try {
      stringified = JSON.stringify(e, Object.getOwnPropertyNames(e as object));
    } catch {
      stringified = String(e);
    }
    console.log('prepareTauriDocument caught error:', {
      error: e,
      type: typeof e,
      message: (e as Error)?.message,
      name: (e as Error)?.name,
      stack: (e as Error)?.stack,
      stringified,
    });

    // Check if this is a network error and file isn't cached - show offline message
    // Use fetch-wrapper's online state which is more reliable than navigator.onLine in Tauri
    // Note: Tauri/Rust errors often come as strings, not Error objects
    const errorMessage = (
      typeof e === 'string' ? e : (e as Error)?.message || ''
    ).toLowerCase();
    const isNetworkError =
      errorMessage.includes('network') ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('fetch failed') ||
      errorMessage.includes('failed to send request') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('dns') ||
      errorMessage.includes('no such host') ||
      errorMessage.includes('unreachable') ||
      !getIsOnline();

    if (isNetworkError) {
      // Report the network error to update global online state
      reportNetworkError();

      // Re-check cache status - if not cached, show offline-specific error
      const isCachedNow = await isFileCached(props.fileUrl);
      if (!isCachedNow) {
        error = { type: 'OfflineNotCached' };
        loading = false;
        return {
          data: undefined,
          type,
          error,
          loading,
          progress,
        };
      }
    }

    error = {
      type: 'DownloadError',
      error: e as Error,
    };
    loading = false;
    return {
      data: undefined,
      type,
      error,
      loading,
      progress,
    };
  }
};
